"use client";

import { useActionState, useState } from "react";
import { useFormStatus } from "react-dom";
import { foundingFeeCents, type Tier } from "@/lib/tiers";
import { money } from "@/lib/money";
import { corporateQuoteAction } from "@/app/actions/leads";

// Corporate is the only open-ended tier: spending power starts at the tier's
// floor and can be incremented with no upper cap. The one-time fee scales with
// the chosen power at the tier's stated rate (fee per dollar of power).
const STEP_CENTS = 100_000_00; // $100K per click

function QuoteSubmit() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className="w-full py-3 text-sm font-display font-bold rounded transition-colors bg-cl-blue text-cl-bg hover:bg-cl-blue/90 disabled:opacity-40"
    >
      {pending ? "Sending..." : "Send Request"}
    </button>
  );
}

export function CorporateCard({
  t,
  founding,
}: {
  t: Tier;
  founding: boolean;
}) {
  const [showForm, setShowForm] = useState(false);
  const [state, formAction] = useActionState(corporateQuoteAction, undefined);
  const floorCents = t.spendingPowerCents; // $500K
  const [powerCents, setPowerCents] = useState(floorCents);
  // Draft holds the raw digits while the field is focused so typing stays
  // smooth; when blurred we show the comma-formatted dollar amount.
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");

  // Fee per cent of spending power, derived from the tier's defined ratio so a
  // custom amount stays proportional to the published Corporate rate (~1%).
  const rate = t.monthlyFeeCents / t.spendingPowerCents;
  const baseFeeCents = Math.round(powerCents * rate);
  const feeCents = founding ? foundingFeeCents(baseFeeCents) : baseFeeCents;

  const displayValue = focused
    ? draft
    : Math.round(powerCents / 100).toLocaleString("en-US");

  const dec = () =>
    setPowerCents((c) => Math.max(floorCents, c - STEP_CENTS));
  const inc = () => setPowerCents((c) => c + STEP_CENTS);

  const onFocus = () => {
    setFocused(true);
    setDraft(String(Math.round(powerCents / 100)));
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    setDraft(digits);
    if (digits) setPowerCents(Number(digits) * 100);
  };
  const onBlur = () => {
    setFocused(false);
    const dollars = Number(draft || "0");
    setPowerCents(Math.max(floorCents, Math.round(dollars) * 100));
  };

  return (
    <div className="relative border rounded p-6 flex flex-col border-cl-border bg-cl-surface">
      <p className="text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-3">
        {t.name}
      </p>

      <div className="mb-1">
        <span className="font-mono font-bold text-4xl text-cl-text">
          {money(feeCents, 0)}
        </span>
      </div>

      {founding ? (
        <>
          <p className="text-cl-muted text-xs font-mono mb-6">
            <span className="line-through">{money(baseFeeCents, 0)}</span>{" "}
            regular rate
          </p>
        </>
      ) : (
        <p className="text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-6">
          Custom rate, scales with power
        </p>
      )}

      {/* Custom spending-power selector: increment above the $500K floor. */}
      <div className="bg-cl-bg border border-cl-border rounded p-4 mb-6 text-center">
        <p className="text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
          Spending Power
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={dec}
            disabled={powerCents <= floorCents}
            aria-label="Decrease spending power"
            className="w-8 h-8 rounded border border-cl-border text-cl-text text-lg leading-none transition-colors hover:border-cl-blue hover:text-cl-blue disabled:opacity-30 disabled:hover:border-cl-border disabled:hover:text-cl-text"
          >
            &minus;
          </button>
          <div className="flex items-center font-mono font-bold text-2xl text-cl-blue">
            <span>$</span>
            <input
              type="text"
              inputMode="numeric"
              aria-label="Spending power in dollars"
              value={displayValue}
              onFocus={onFocus}
              onChange={onChange}
              onBlur={onBlur}
              // Grow with the number so commas past $999,999 are not clipped.
              style={{ width: `${Math.max(4, displayValue.length)}ch` }}
              className="bg-transparent text-center outline-none focus:text-cl-text"
            />
          </div>
          <button
            type="button"
            onClick={inc}
            aria-label="Increase spending power"
            className="w-8 h-8 rounded border border-cl-border text-cl-text text-lg leading-none transition-colors hover:border-cl-blue hover:text-cl-blue"
          >
            +
          </button>
        </div>
      </div>

      <div className="space-y-3 flex-1 mb-6">
        {t.perks.map((p) => (
          <div key={p} className="flex items-start gap-2">
            <svg
              className="w-3.5 h-3.5 text-cl-blue mt-0.5 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="text-cl-muted text-xs">{p}</span>
          </div>
        ))}
      </div>

      {/* Corporate is not self-serve checkoutable: buyers request a quote and
          the team follows up. The stepper above still customizes the ask. */}
      {state?.ok ? (
        <div className="rounded border border-cl-blue/30 bg-cl-blue/5 p-4 text-center">
          <p className="font-display font-bold text-sm text-cl-text mb-1">
            Request received
          </p>
          <p className="text-cl-muted text-xs">
            We&apos;ll reach out within one business day to build your quote.
          </p>
        </div>
      ) : showForm ? (
        <form action={formAction} className="space-y-3">
          <input type="hidden" name="power" value={powerCents} />
          <input
            type="email"
            name="email"
            required
            placeholder="you@company.com"
            aria-label="Email"
            className="w-full bg-cl-bg border border-cl-border rounded px-3 py-2.5 text-sm text-cl-text placeholder:text-cl-muted/40 focus:border-cl-blue focus:outline-none transition-colors"
          />
          <input
            type="tel"
            name="phone"
            required
            placeholder="Phone number"
            aria-label="Phone number"
            className="w-full bg-cl-bg border border-cl-border rounded px-3 py-2.5 text-sm text-cl-text placeholder:text-cl-muted/40 focus:border-cl-blue focus:outline-none transition-colors"
          />
          {state?.error ? (
            <p className="text-red-400 text-xs font-mono">{state.error}</p>
          ) : null}
          <QuoteSubmit />
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="group w-full py-3 text-sm font-display font-bold rounded transition-colors border border-cl-border text-cl-text hover:border-cl-blue hover:text-cl-blue"
        >
          Request a Quote
          <svg
            className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform inline"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      )}
    </div>
  );
}
